import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type UnitsResponse, type UnitsSearchParams } from './types';
import { zodUnitsSearchParamsSchema } from './validations';

/**
 * Fetches all defined UN/CEFACT units from the API.
 * @param language - The language to fetch the units in.
 * @returns The mapped UN/CEFACT unit.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/get_complete_unit_mapping_dictionary_units_dictionary_get
 */
export const fetchUnits = async ({
  language = 'DE',
  unitType,
}: UnitsSearchParams) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.UNITS.GET_ALL(), {
        searchParams: {
          language,
          unitType,
        },
        validate: { searchParams: zodUnitsSearchParamsSchema },
      })
      .json<UnitsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching units',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.unit.fetchUnits.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.unit.fetchUnits.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
